import { type BookmakersConfig } from '@cx-modules/types';

import { type SectionProps } from '~/apps/product-plus/components/BettingInsights/components/ResultTable';
import { type RequestContext } from '~/types/RequestContext';

import { type BettingInsightsContent, type EsIndex } from '../types';

import { type BettingInsightsUI, type StyleSheet } from './BettingInsightsUI';

// Type for the context that will be passed as first parameter to render functions
export interface BettingInsightsTableBodyContext {
  environment: RequestContext['environment'];
  featureFlags: RequestContext['featureFlags'];
  repubConfig: RequestContext['repubConfig'];
  user: RequestContext['user'];
}

export interface BettingInsightsTableBodyConfig<
  TIndex extends EsIndex,
  TContent extends BettingInsightsContent<TIndex>,
> {
  render: (
    context: BettingInsightsTableBodyContext,
    props: {
      bookmakers: BookmakersConfig;
      content: TContent;
      label: string;
      resourcesUrl?: string;
      resultIndex: number;
      sectionProps: SectionProps;
      styles: StyleSheet;
      table: BettingInsightsUI<TIndex, TContent>['card']['table'];
    },
  ) => React.ReactNode;
}

// Type alias for the return type of createBettingInsightsTableBody
export type BettingInsightsTableBody<
  TIndex extends EsIndex,
  TContent extends BettingInsightsContent<TIndex>,
> = Readonly<{
  render: (props: {
    bookmakers: BookmakersConfig;
    content: TContent;
    label: string;
    resourcesUrl?: string;
    resultIndex: number;
    sectionProps: SectionProps;
    styles: StyleSheet;
    table: BettingInsightsUI<TIndex, TContent>['card']['table'];
  }) => React.ReactNode;
}>;

/**
 * Factory function to create BettingInsightsTableBody configuration (functional approach)
 * @param requestContext - The request context containing environment, feature flags, repub config, and user details
 * @param config - The table body configuration with render function
 * @returns Immutable table body configuration object with bound render method
 * @example
 * ```typescript
 * const body = createBettingInsightsTableBody(requestContext, {
 *   render(context, { bookmakers, content, label, resourcesUrl, resultIndex, sectionProps, styles, table }) {
 *     // context has access to environment, featureFlags, repubConfig, user
 *     return (
 *       <div className={styles.body}>
 *         {content.map((item, index) => (
 *           <div key={index}>{item.name}</div>
 *         ))}
 *       </div>
 *     );
 *   }
 * });
 * ```
 */
export function createBettingInsightsTableBody<TIndex extends EsIndex, TContent extends BettingInsightsContent<TIndex>>(
  requestContext: RequestContext,
  config: BettingInsightsTableBodyConfig<TIndex, TContent>,
): BettingInsightsTableBody<TIndex, TContent> {
  // Create context object
  const context: BettingInsightsTableBodyContext = {
    environment: requestContext.environment,
    featureFlags: requestContext.featureFlags,
    repubConfig: requestContext.repubConfig,
    user: requestContext.user,
  };

  // Create the result object with wrapper function that injects context
  const result = {
    // Wrapper function that injects context as first parameter
    render: (props: {
      bookmakers: BookmakersConfig;
      content: TContent;
      label: string;
      resourcesUrl?: string;
      resultIndex: number;
      sectionProps: SectionProps;
      styles: StyleSheet;
      table: BettingInsightsUI<TIndex, TContent>['card']['table'];
    }) => config.render(context, props),
  };

  return Object.freeze(result);
}
